import React, { useState } from 'react';
import { X, CalendarDays, Sliders, MessageSquare, IndianRupee, Loader2 } from 'lucide-react';
import styles from './Loans.module.css';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

export const SkipEmiModal = ({ isOpen, onClose, loanId, emiNumber, onSubmitted }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Please provide a reason for skipping this EMI');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/loans/${loanId}/skip-request`, {
        emi_number: emiNumber,
        reason: reason.trim()
      });
      toast.success('Skip request submitted for approval');
      onSubmitted();
      onClose();
    } catch (error) {
      console.error('Error requesting EMI skip:', error);
      toast.error(error.response?.data?.message || 'Failed to submit skip request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-backdrop']} onClick={onClose}>
      <div className={styles['modal-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['modal-header']}>
          <div className={styles['modal-title-group']}>
            <h2>Request EMI Skip</h2>
            <p>Submit request to skip EMI #{emiNumber} for this month.</p>
          </div>
          <button className={styles['close-icon-btn']} onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles['modal-body']}>
            <div className={styles['form-group']}>
              <label><MessageSquare size={16} /> Reason for Skip *</label>
              <textarea 
                className={styles['form-input']}
                style={{ minHeight: '120px', resize: 'vertical' }}
                placeholder="e.g. Salary delay, emergency deduction request..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>

          <div className={styles['modal-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose} style={{ maxWidth: '120px' }}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading} style={{ maxWidth: '200px' }}>
              {loading ? <Loader2 size={18} className="spinner" /> : <CalendarDays size={18} />}
              <span>Submit Request</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const AdjustEmiModal = ({ isOpen, onClose, loanId, onSubmitted }) => {
  const [newEmiAmount, setNewEmiAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(newEmiAmount);
    if (isNaN(amt) || amt < 500) {
      toast.error('EMI amount must be at least ₹500');
      return;
    }
    if (!reason.trim()) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/loans/${loanId}/adjust-emi`, {
        new_emi_amount: amt,
        reason: reason.trim()
      });
      toast.success('EMI amount adjusted successfully');
      onSubmitted();
      onClose();
    } catch (error) {
      console.error('Error adjusting EMI:', error);
      toast.error(error.response?.data?.message || 'Failed to adjust EMI');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-backdrop']} onClick={onClose}>
      <div className={styles['modal-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['modal-header']}>
          <div className={styles['modal-title-group']}>
            <h2>Adjust Monthly EMI</h2>
            <p>Recalculate remaining payment amounts for all upcoming EMIs.</p>
          </div>
          <button className={styles['close-icon-btn']} onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles['modal-body']}>
            {/* New EMI Amount */}
            <div className={styles['form-group']}>
              <label><IndianRupee size={16} /> New EMI Amount (₹) *</label>
              <input 
                type="number"
                className={styles['form-input']}
                placeholder="e.g. ₹4,000"
                value={newEmiAmount}
                onChange={(e) => setNewEmiAmount(e.target.value)}
              />
            </div>

            {/* Reason */}
            <div className={styles['form-group']} style={{ marginTop: '20px' }}>
              <label><MessageSquare size={16} /> Adjustment Reason *</label>
              <textarea 
                className={styles['form-input']}
                style={{ minHeight: '100px', resize: 'vertical' }}
                placeholder="e.g. Salary reduction, request from employee..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>

          <div className={styles['modal-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose} style={{ maxWidth: '120px' }}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading} style={{ maxWidth: '200px' }}>
              {loading ? <Loader2 size={18} className="spinner" /> : <Sliders size={18} />}
              <span>Adjust EMI</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
